/** Build Meta Pixel commerce params (value, currency, content_type, content_ids). */

function toFiniteNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function pickFirstFinite(...candidates) {
  for (const candidate of candidates) {
    const n = toFiniteNumber(candidate);
    if (n != null && n >= 0) return n;
  }
  return null;
}

export function normalizeMetaCurrency(currency, fallback = "ILS") {
  const raw = String(currency || fallback).trim().toUpperCase();
  return raw || fallback;
}

export function buildMetaCommerceParams({
  value = null,
  currency = "ILS",
  content_type = null,
  content_ids = [],
  content_name = null,
  search_string = null,
  num_items = null,
} = {}) {
  const payload = {};
  const resolvedValue = toFiniteNumber(value);
  if (resolvedValue != null) payload.value = resolvedValue;
  if (currency) payload.currency = normalizeMetaCurrency(currency);
  if (content_type) payload.content_type = content_type;
  const ids = (Array.isArray(content_ids) ? content_ids : [content_ids])
    .map((id) => (id == null ? "" : String(id).trim()))
    .filter(Boolean);
  if (ids.length) payload.content_ids = ids;
  if (content_name) payload.content_name = String(content_name);
  if (search_string) payload.search_string = String(search_string);
  if (num_items != null && Number.isFinite(Number(num_items))) {
    payload.num_items = Number(num_items);
  }
  return payload;
}

export function metaCommerceFromHotelProfile(hotelInfo, { hotelId = null, value = null } = {}) {
  if (!hotelInfo && !hotelId) return {};
  const id =
    hotelId ??
    hotelInfo?.HotelId ??
    hotelInfo?.hotel_id ??
    hotelInfo?.hotelId ??
    null;
  return buildMetaCommerceParams({
    value,
    currency: "ILS",
    content_type: "hotel",
    content_ids: id ? [id] : [],
    content_name: hotelInfo?.HotelName || hotelInfo?.hotelname || null,
  });
}

export function metaCommerceFromPackageSelection(selectedResult, { value = null } = {}) {
  if (!selectedResult) return {};
  const dealId =
    selectedResult.dealId ??
    selectedResult.id ??
    selectedResult.ldsDealId ??
    selectedResult.packageId ??
    null;
  const hotelId = selectedResult?.hotel?.hotelId ?? selectedResult?.hotel?.hotel_id ?? null;
  const contentIds = [dealId, hotelId].filter(Boolean).map(String);
  const resolvedValue =
    value ??
    pickFirstFinite(
      selectedResult?.pricing?.finalIls,
      selectedResult?.finalPriceIls,
      selectedResult?.totalPrice,
      selectedResult?.ourTotalFare?.finalPriceGBP
    );
  return buildMetaCommerceParams({
    value: resolvedValue,
    currency: "ILS",
    content_type: "vacation_package",
    content_ids: contentIds.length ? contentIds : dealId ? [dealId] : [],
    content_name:
      selectedResult?.hotel?.hotelName ??
      selectedResult?.packageName ??
      selectedResult?.title ??
      null,
  });
}

export function metaCommerceFromOrder(order, { value = null } = {}) {
  if (!order) return {};
  const orderId = order.id ?? order.order_id ?? order.orderId ?? null;
  const dealType = String(order.deal_type || order.dealtype || "").trim();
  const contentType = (() => {
    if (/hotel/i.test(dealType) && !/package|flight/i.test(dealType)) return "hotel";
    if (/cruise/i.test(dealType)) return "product";
    if (/flight/i.test(dealType) && /hotel|package|dynamic/i.test(dealType)) {
      return "vacation_package";
    }
    if (/package|vacation|organized|dynamic|lds/i.test(dealType)) return "vacation_package";
    return "product";
  })();
  const hotelId =
    order.hotel_id ??
    order.hotelId ??
    order.hotel?.hotelId ??
    order.hotel?.hotel_id ??
    null;
  const packageId =
    order.package_id ??
    order.packageId ??
    order.hotelpackage_id ??
    order.ldsorderid ??
    null;
  const contentIds = [orderId, packageId, hotelId].filter(Boolean).map(String);
  const resolvedValue =
    value ??
    pickFirstFinite(
      order.sumpaid,
      order.paid_amount,
      order.total_price,
      order.finalprice,
      order.packageprice,
      order.totalPrice
    );
  const currency =
    order.currency ||
    order.supplier_currency ||
    (contentType === "hotel" || contentType === "vacation_package" ? "ILS" : "ILS");
  return buildMetaCommerceParams({
    value: resolvedValue,
    currency,
    content_type: contentType,
    content_ids: contentIds,
    content_name:
      order.hotel?.hotelname ??
      order.hotel?.hotelName ??
      order.hotel_name ??
      order.itemName ??
      null,
  });
}

export function metaCommerceFromSearchContext({
  searchTab,
  searchPhrase,
  location,
  origin,
  totalResults = null,
} = {}) {
  const parts = [];
  if (searchPhrase?.hotelName) parts.push(String(searchPhrase.hotelName));
  if (searchPhrase?.city) parts.push(String(searchPhrase.city));
  if (searchPhrase?.country) parts.push(String(searchPhrase.country));
  if (!parts.length && location) {
    const locName =
      typeof location === "string"
        ? location
        : location?.name || location?.city || location?.label;
    if (locName) parts.push(String(locName));
  }
  if (origin && searchTab === "PLANES") {
    const originName =
      typeof origin === "string" ? origin : origin?.name || origin?.city;
    if (originName) parts.unshift(String(originName));
  }
  const searchString = parts.filter(Boolean).join(", ") || null;
  return buildMetaCommerceParams({
    content_type: "product",
    search_string: searchString,
    num_items: totalResults,
    content_ids: searchTab ? [String(searchTab)] : [],
  });
}
